/**
 * Centralised date/time formatter for tables across the Dashboard, Accounts and
 * Contacts modules. One implementation so every table reads the same way.
 *
 * Output — a MOMENT IN TIME, date and clock together:
 *   • current year         → "8 Apr, 3:03 PM"
 *   • any other year       → "12 Dec 2025, 2:36 PM"
 *   • date with no clock   → "8 Apr" / "12 Dec 2025"
 *   • empty / unparseable  → the em-dash placeholder "—"
 *
 * This is console-v2's own recipe. Its listings build the Created column as
 *
 *     datePart = date.toLocaleDateString('en-AU', {day: 'numeric', month: 'short'})
 *                + (date.getFullYear() !== curYear ? ' ' + date.getFullYear() : '')
 *     timePart = date.toLocaleTimeString('en-AU',
 *                  {hour: 'numeric', minute: '2-digit', hour12: true}).toUpperCase()
 *     created  = datePart + ', ' + timePart
 *
 * (`sample-requests-list.component.ts`, and the identical block in
 * `account-detail.component.ts`; `account-list.fmtWhen()` uses the same shape for
 * Last Login / Last Quote / Last Order). Its hard-coded listing rows read exactly
 * that way — "15 Mar, 10:28 AM", "8 Apr, 3:03 PM", "12 Dec 2025, 2:36 PM" — so the
 * year is dropped only for the CURRENT year and the clock is always present.
 *
 * The day is `numeric`, not `2-digit`: the reference renders "8 Apr", not "08 Apr".
 *
 * This function previously returned the date alone. That was wrong: it was reasoned
 * from the reference's `toLocaleDateString` calls, which genuinely cannot emit a
 * time, without noticing that the Created and Last-* columns append a separately
 * built `toLocaleTimeString` to them. The clock is part of the design in every one
 * of those columns, and dropping it lost real API data.
 *
 * A value carrying NO clock (a bare `YYYY-MM-DD`) still renders date-only — there
 * is no time in the source to show, and inventing "12:00 AM" for it is the one
 * genuine problem the date-only version was reaching for.
 *
 * Values are read LITERALLY off a `YYYY-MM-DD[ T]HH:MM` string rather than being
 * put through `new Date()`. The PHP API writes these in Australia/Sydney with no
 * zone marker, so parsing them re-interprets them against the viewer's browser zone
 * and can shift a late-evening record onto the next day.
 *
 * A string that NAMES ITS OWN ZONE is the exception, and reading it literally is
 * always wrong: `2026-08-26T05:16:00.000Z` is not 5:16 in anybody's morning, it is
 * an instant that happens to be 3:16 PM in Sydney. The Node API is full of them —
 * every `DateTime` column comes back through Prisma as UTC ISO — which is how the
 * internal-note timestamps on the new Accounts/Contacts screens came to show raw
 * UTC, ten hours out, while the legacy notes panel (which puts the same value
 * through `moment()`) converted it. Such values go down the `Intl` path and are
 * resolved in Australia/Sydney, the same as a real `Date` or a timestamp.
 *
 * So: no zone marker → the digits ARE the local wall clock, shown as-is; a zone
 * marker → a real instant, converted to Australia/Sydney.
 *
 * For columns holding a CALENDAR DATE rather than a moment in time — the Stock
 * Required Date, and Registered — use `formatTableDateOnly`: it always shows the
 * year and never a clock. See its own note below.
 */
const TABLE_DATE_TZ = 'Australia/Sydney';

/** Placeholder used when there is no date to show. */
export const TABLE_DATE_EMPTY = '-';

/**
 * A date string that names its own timezone — a trailing `Z`, or a `±HH:MM` /
 * `±HHMM` offset. Such a value denotes a real INSTANT, so its digits are not
 * local wall clock and must not be read literally; it is converted instead.
 *
 * Anchored at the end so it cannot fire on the hyphens inside a date: the `-26`
 * of `2026-08-26` has no second digit pair after it, and the `:00` of
 * `2026-08-26 05:16:00` is preceded by a colon, not a sign.
 */
const EXPLICIT_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/** Month abbreviations, indexed 1-12, matching `formatTableDate`'s en-US "short". */
const MONTHS_SHORT = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Day/month/year parts in the target timezone. en-US is used deliberately: its
 * "short" month is a clean 3-letter abbreviation for every month (Jan…Dec,
 * incl. "Sep"), unlike en-AU/en-GB which render "June", "July", "Sept". We then
 * reassemble in day-month order ("08 Jun") ourselves.
 */
function dateParts(d: Date): {day: string; month: string; year: string; hour: string; minute: string} {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: TABLE_DATE_TZ,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';
  return {day: get('day'), month: get('month'), year: get('year'), hour: get('hour'), minute: get('minute')};
}

/** Current year in the target timezone, for the drop-the-year test. */
function currentYear(): string {
  return new Intl.DateTimeFormat('en-US', {timeZone: TABLE_DATE_TZ, year: 'numeric'}).format(new Date());
}

/** "3:03 PM" from a 24-hour hour/minute pair, matching the reference's uppercase. */
function clock(hour24: number, minute: string): string {
  const meridiem = hour24 >= 12 ? 'PM' : 'AM';
  const hour = hour24 % 12 || 12;
  return `${hour}:${minute} ${meridiem}`;
}

/**
 * Shared assembly for both moment-in-time formatters: "8 Apr", "12 Dec 2025",
 * plus ", 3:03 PM" when there is a clock to show.
 */
function assemble(day: string, month: string, year: string, time: string | null): string {
  let out = `${+day} ${month}`;
  if (year !== currentYear()) {
    out += ' ' + year;
  }
  return time ? `${out}, ${time}` : out;
}

export function formatTableDate(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return TABLE_DATE_EMPTY;
  }

  // Preferred path: read the parts straight off the string, so a zone-less
  // Sydney timestamp cannot be re-interpreted against the viewer's browser zone
  // (which shifts late-evening records onto the next day). The clock group is
  // optional — a bare `YYYY-MM-DD` has no time to show.
  if (typeof value === 'string' && !EXPLICIT_ZONE.test(value.trim())) {
    const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(value.trim());
    if (m) {
      const month = MONTHS_SHORT[+m[2]];
      if (month) {
        const time = m[4] !== undefined ? clock(+m[4], m[5]) : null;
        return assemble(m[3], month, m[1], time);
      }
    }
  }

  // Fallback for a real Date, a timestamp, or an ISO value carrying a zone (the
  // Node API's UTC timestamps land here): resolve it in Australia/Sydney. `Intl` always yields a clock here, and these
  // inputs always denote a real instant, so the time is always shown.
  const d = new Date(value as any);
  if (isNaN(d.getTime())) {
    return TABLE_DATE_EMPTY;
  }
  const {day, month, year, hour, minute} = dateParts(d);
  // `hour12: false` renders midnight as "24" in some ICU versions.
  return assemble(day, month, year, clock(+hour % 24, minute));
}


/**
 * Calendar-date formatter for columns that carry a DATE, not a moment in time —
 * the Stock Required Date being the case that drove this out: "15 Sep 2026".
 *
 * Differs from `formatTableDate` in the two ways that matter for a plain date:
 *
 *   • The YEAR IS ALWAYS SHOWN. `formatTableDate` drops it for the current year,
 *     which is right for "created 3 days ago" but wrong for a forward-looking
 *     date — an SRD reading "15 Sep" gives no hint which year is meant.
 *   • NO TIMEZONE CONVERSION. A `YYYY-MM-DD` (or ISO midnight) value names a
 *     calendar day, so it is read literally off the string. Handing it to
 *     `Intl` in Australia/Sydney re-interprets UTC midnight as 10 AM local —
 *     harmless for the time, but it shifts the DAY for any timezone west of
 *     UTC, and appends a time nobody entered.
 *
 * Falls back to the parsed Date only when the input isn't a leading-ISO string
 * (a real Date instance or a timestamp), where there is nothing to read off.
 */
export function formatTableDateOnly(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return TABLE_DATE_EMPTY;
  }

  // Preferred path: pull the calendar day straight out of the string so the
  // date can never drift by a day through a timezone conversion.
  if (typeof value === 'string') {
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
    if (iso) {
      const month = MONTHS_SHORT[+iso[2]];
      if (month) {
        // `+` drops the leading zero — the reference renders "7 Mar 2023", not "07".
        return `${+iso[3]} ${month} ${iso[1]}`;
      }
    }
  }

  const d = new Date(value as any);
  if (isNaN(d.getTime())) {
    return TABLE_DATE_EMPTY;
  }

  const {day, month, year} = dateParts(d);
  return `${+day} ${month} ${year}`;
}

/**
 * Format a stored "Last Login"-style timestamp for display WITHOUT any timezone
 * conversion. These values are written by the API in Australia/Sydney time as a
 * timezone-less `YYYY-MM-DD HH:MM(:SS)` string; feeding them through `new Date()` / Intl
 * re-interprets them against the viewer's browser timezone and can shift a late-night
 * login onto the next morning. Here the parts are read straight off the string —
 * "12 Aug, 11:59 PM" — so it reads identically on any machine. Year is shown only when it
 * isn't the current year (matching `formatTableDate`). Returns '' for empty input and
 * falls back to `formatTableDate` for values that aren't a leading date-time string.
 *
 * Single source of truth for both the contact detail page and the contacts list, which
 * had drifted (the list still showed `last_activity`, so new contacts wrongly showed a
 * "Last Login").
 */
export function formatSydneyDateTime(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  // `formatTableDate` now reads zone-less strings literally and appends the clock
  // itself — the two had grown identical bodies, and one of them silently kept a
  // leading-zero day ("08 Jun") after the other moved to the reference's numeric
  // one ("8 Jun"), so the same instant rendered differently on the contacts list
  // and the contact detail beside it. Delegating keeps a single implementation;
  // only the empty-value convention differs, which is why this wrapper stays.
  return formatTableDate(value);
}

/**
 * The Gather Commitment credit ledger's own two shapes. This one table is the
 * exception to the "D Mon" house style: the reference renders its ledger in
 * numeric slashes, and its Completed On cell in 24-hour time
 * (`account-detail.component.ts`):
 *
 *     date:        toLocaleDateString('en-AU', {day:'2-digit', month:'2-digit', year:'numeric'})
 *     completedOn: <that> + ' ' + toLocaleTimeString('en-AU', {hour:'2-digit', minute:'2-digit', hour12:false})
 *
 * giving "10/11/2025" and "10/11/2025 14:36". A ledger is a financial register
 * read down a column, where fixed-width numerics line up and a variable-width
 * "8 Apr" does not, so the departure is deliberate rather than drift.
 *
 * Parts are read literally off the string for the same zone reason as
 * `formatTableDate`.
 */
function ledgerParts(value: unknown): {d: string; m: string; y: string; hh?: string; mm?: string} | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const s = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(String(value).trim());
  if (s) {
    return {d: s[3], m: s[2], y: s[1], hh: s[4], mm: s[5]};
  }
  const date = new Date(value as any);
  if (isNaN(date.getTime())) {
    return null;
  }
  const p = new Intl.DateTimeFormat('en-GB', {
    timeZone: TABLE_DATE_TZ, day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(date);
  const get = (t: string) => p.find(x => x.type === t)?.value ?? '';
  return {d: get('day'), m: get('month'), y: get('year'), hh: get('hour'), mm: get('minute')};
}

/** "10/11/2025" — the ledger's Date column. */
export function formatLedgerDate(value: unknown): string {
  const p = ledgerParts(value);
  return p ? `${p.d}/${p.m}/${p.y}` : TABLE_DATE_EMPTY;
}

/** "10/11/2025 14:36" — the ledger's Completed On column; date alone if no clock. */
export function formatLedgerDateTime(value: unknown): string {
  const p = ledgerParts(value);
  if (!p) {
    return TABLE_DATE_EMPTY;
  }
  const date = `${p.d}/${p.m}/${p.y}`;
  // `hour12: false` can render midnight as "24" in some ICU versions.
  return p.hh !== undefined ? `${date} ${String(+p.hh % 24).padStart(2, '0')}:${p.mm}` : date;
}

/**
 * The OLD-design internal-notes stamp — "26/08/2026 03:04 PM" — resolved in
 * Australia/Sydney.
 *
 * The notes panel (`admin-core/components/internal-notes`, and the milestone
 * tooltips beside it on Sample Requests, Quotes and Orders) keeps the slashed
 * `DD/MM/YYYY hh:mm A` shape; only the ZONE was wrong. It built that string with
 * a bare `moment(value).format(...)`, which renders in the VIEWER'S browser zone.
 * Every note, reply and milestone note is written by the Node API as
 * `created_at: new Date()` and comes back through Prisma as UTC ISO
 * (`2026-08-26T05:04:00.000Z`), so the panel showed 10:34 AM on a machine in
 * IST and 3:04 PM on one in Sydney — the same note, two different times, and
 * only one of them the Australian business time the console reports in.
 *
 * This is the same zone rule `formatTableDate` applies for the NEW-design note
 * stamps on the Account and Contact detail pages (`noteWhen()`), which is where
 * the correct time is already displayed; the only difference here is the output
 * format, which stays as the old design has it.
 *
 *   • a string naming its own zone (trailing `Z` or `±HH:MM`) is a real INSTANT
 *     → converted to Australia/Sydney
 *   • a zone-less `YYYY-MM-DD HH:MM` is already Sydney wall clock (the PHP API
 *     writes these) → read literally, never re-interpreted against the browser
 *
 * `withTime: false` drops the clock, matching the callers' existing flag.
 * Returns '' — not the em dash — for an empty value, because these call sites
 * render the stamp bare next to the author and a stray "—" would read as a
 * broken note.
 */
export function formatNoteDateTime(value: unknown, withTime: boolean = true): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const raw = String(value).trim();
  if (raw === '') {
    return '';
  }

  // `hh:mm A` — a LEADING-ZERO 12-hour clock, which is what `moment`'s `hh` gave
  // and what the panel has always rendered ("03:04 PM", not "3:04 PM"). The
  // shared `clock()` above is the reference's numeric-hour form and is wrong here.
  const stamp = (hour24: number, minute: string) => {
    const meridiem = hour24 >= 12 ? 'PM' : 'AM';
    return `${String(hour24 % 12 || 12).padStart(2, '0')}:${minute} ${meridiem}`;
  };

  if (!EXPLICIT_ZONE.test(raw)) {
    const literal = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(raw);
    if (literal) {
      const date = `${literal[3]}/${literal[2]}/${literal[1]}`;
      return withTime && literal[4] !== undefined
        ? `${date} ${stamp(+literal[4], literal[5])}`
        : date;
    }
  }

  const parsed = new Date(value as any);
  if (isNaN(parsed.getTime())) {
    return '';
  }
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TABLE_DATE_TZ,
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(parsed);
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';
  const date = `${get('day')}/${get('month')}/${get('year')}`;
  // `hour12: false` renders midnight as "24" in some ICU versions.
  return withTime ? `${date} ${stamp(+get('hour') % 24, get('minute'))}` : date;
}

/**
 * The Australia/Sydney CALENDAR DAY a stored timestamp falls on, as a
 * comparable `YYYYMMDD` number (20260508 for 8 May 2026). `null` when there is
 * no date to read.
 *
 * Exists so the date-range filters on the record screens (Internal Notes
 * "Posted", History "When") compare the same day the row RENDERS as. The
 * formatters above already resolve the zone correctly — this applies the
 * identical rule and hands back a value that can be compared instead of one
 * that can only be shown:
 *
 *   • no zone marker → the digits ARE Sydney wall clock, read literally
 *   • a zone marker  → a real instant, converted to Australia/Sydney
 *
 * Doing this by hand at each call site is how the note table's mixed-timezone
 * writers (the PHP web app writes Sydney wall clock into the same column the
 * Node API writes UTC into) produce a range filter that drops a late-evening
 * note from the day it visibly belongs to.
 */
export function sydneyCalendarDay(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const raw = String(value).trim();
  if (raw === '') {
    return null;
  }

  if (!EXPLICIT_ZONE.test(raw)) {
    const literal = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
    if (literal) {
      return +literal[1] * 10000 + +literal[2] * 100 + +literal[3];
    }
  }

  const parsed = new Date(value as any);
  if (isNaN(parsed.getTime())) {
    return null;
  }
  const {day, month, year} = dateParts(parsed);
  const monthIndex = MONTHS_SHORT.indexOf(month);
  if (monthIndex < 1) {
    return null;
  }
  return +year * 10000 + monthIndex * 100 + +day;
}

/**
 * Whole Sydney calendar days from a stored timestamp to `now`: 0 on the day it
 * falls on, 1 on the next, and so on. Read with `sydneyCalendarDay`, so it counts
 * from the same day `formatTableDate` shows the timestamp on. `null` when there
 * is no date to read.
 */
export function sydneyCalendarDaysSince(value: unknown, now: Date = new Date()): number | null {
  const from = sydneyCalendarDay(value);
  const to = sydneyCalendarDay(now);
  if (from === null || to === null) {
    return null;
  }
  const utcMidnight = (key: number) => Date.UTC(Math.floor(key / 10000), Math.floor(key / 100) % 100 - 1, key % 100);
  return Math.round((utcMidnight(to) - utcMidnight(from)) / 86400000);
}

/**
 * The same `YYYYMMDD` key for a `Date` picked in the BROWSER's zone — what the
 * date-range filter emits for the ends of a range. Its `from`/`to` are local
 * midnight boundaries the user chose off a calendar, so the calendar day is
 * read off the local parts rather than converted: converting would move
 * "1 July" by a day for any viewer west of Sydney.
 */
export function localCalendarDay(date: Date): number {
  return date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
}

/**
 * "1 Jul 2026 — 30 Jun 2027" — the label a date-range filter's active chip
 * carries, the same shape every listing prints and the same `en-AU` call
 * console-v2's own `rangeLabel` makes.
 *
 * Formats the two `Date`s the picker emits, in the BROWSER's zone, for the
 * same reason `localCalendarDay` reads them locally: they are calendar
 * boundaries the user chose, not instants to be converted.
 */
export function formatRangeLabel(from: Date, to: Date): string {
  const fmt = (date: Date) =>
    date.toLocaleDateString('en-AU', {day: 'numeric', month: 'short', year: 'numeric'});
  return `${fmt(from)} - ${fmt(to)}`;
}
